/* global globalThis */
// Волна 35 (G; у кого действует — checkIfW35GarrisonPlayer у вызывающего кода:
// боты с бонусом «большой» и выше вне legacy-группы A/B): гарнизоны по
// доктрине фракции — «защитный класс» отрядов. Отряд-гарнизон держит кварталы
// своего города, пока город открыт: не берет кампании, не участвует в
// форсированных маршах, не тянется к фронту (decideMovementStep,
// decideManeuverStep, campaigns). В режиме 'hard' он и ходит только по кварталам
// города, в 'soft' (по умолчанию) — получает на них ценность держателя и уходит
// лишь на ход ценнее (getGarrisonLockMode). Константы и доктрины — configs/botGarrison.js.
//
// Откуда (реплеи прода с 05.10): угрожаемый город бота стоит пустым в 2.7 раза
// чаще, чем у сильного человека, и его берут в 9 случаях из 10; резерва в 1–2
// клетках нет. Экономические фракции держат города не лучше агрессивных. Модель
// угрозы волны 32 реагирует только на врага в одном ходе, поэтому без угрозы
// последний держатель уходил по градиенту в 69–74% случаев.
import {
  selectCitiesCache,
  selectFinalRound,
  selectGameType,
  selectGoblinsTrapDeathHexKeys,
  selectHexes,
  selectNextInitiativePlayerId,
  selectPendingMovementActions,
  selectPhase,
  selectPhaseCompletedPlayers,
  selectPlayers,
  selectRound,
  selectTurnOrder,
  selectUnits,
  selectWarPhaseStage,
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calculateAvailableMovementCells} from "../../gameState/calculateAvailableMovementCells";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {getCityHexKeys} from "../../gameState/getCityHexKeys";
import {getFarmsOfPlayerCities} from "../../gameState/getPlayerFarms";
import {getHexDistance} from "../../coords/getHexDistance";
import {getUnitTypeConfig} from "../../../configs/units";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {POLICIES} from "../../../constants/policies";
import {isHexTokenIsBlockerForMovement} from "../../movement/isHexTokenIsBlockerForMovement";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {isUnitInsectoid} from "../../factions/insectoids/isUnitInsectoid";
import {getDamageDiceFaceOutcomes} from "../../dice/getDamageDiceFaceOutcomes";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {
  DEFAULT_GARRISON_DOCTRINE,
  GARRISON_DOCTRINES,
  getFactionGarrisonDoctrine,
  getStricterGarrisonDoctrine,
  W35_GARRISON_AGGRESSIVE_MAX_UNIT_SHARE,
  W35_GARRISON_DOOMED_DEATH_CHANCE,
  W35_GARRISON_EXPOSURE_DISTANCE,
  W35_GARRISON_EXPOSURE_MODE,
  W35_GARRISON_EXPOSURE_MODES,
  W35_GARRISON_LOCK_MODE,
  W35_GARRISON_LOCK_MODES,
  W35_GARRISON_MIN_INTENSITY,
  W35_GARRISON_MIXED_MIN_CITY_WIN_POINTS,
  W35_GARRISON_STARVATION_LOSS,
  W35_GARRISON_STRICT_MIN_INTENSITY,
} from "../../../configs/botGarrison";
import {getBotBonusProfile} from "../getBotBonusProfile";
import {isPreW35GarrisonPlayer} from "../abTestGates";
import {calcSupplyReachCells, getBotGridRenderer, getNeighborHexKeys} from "../botGrid";
import {getBotStrategy} from "../strategy/botStrategyStore";
import {BOT_EXPECTED_DICE_PENALTY} from "../botConfig";
import {checkIfW42CelectialsStormPlayer} from "../factions/celectialsBot";
import {calcCityHolderValue, CITY_THREAT_LEVELS, getCityCaptureThreats} from "./cityCaptureThreat";

export const GARRISON_EXPOSURE = {
  // Угроза от «скоро» и выше; в режиме 'distance' — еще живой противник рядом
  // (W35_GARRISON_EXPOSURE_DISTANCE)
  OPEN: 'open',
  // Противник рядом, но все подходы прикрыты своими городами и врага в двух ходах нет
  INTERIOR: 'interior',
  // Противника рядом нет; в режиме 'threat' — и угрозы «скоро» и выше нет
  SAFE: 'safe',
};

// Калибровка G без правки кода (глобал харнесса и стендов); в проде оверрайд не задан
const getGarrisonExposureMode = () => (
  Object.values(W35_GARRISON_EXPOSURE_MODES).includes(globalThis.__w35GarrisonExposureMode)
    ? globalThis.__w35GarrisonExposureMode
    : W35_GARRISON_EXPOSURE_MODE
);

/**
 * Режим удержания гарнизона (G v2): 'hard' — фильтр клеток, 'soft' — ценность
 * держателя на кварталах своего города (configs/botGarrison). Калибровка —
 * globalThis.__w35GarrisonLockMode (харнесс и стенды; в проде не задан)
 */
export const getGarrisonLockMode = () => (
  Object.values(W35_GARRISON_LOCK_MODES).includes(globalThis.__w35GarrisonLockMode)
    ? globalThis.__w35GarrisonLockMode
    : W35_GARRISON_LOCK_MODE
);

export const isGarrisonLockHard = () => getGarrisonLockMode() === W35_GARRISON_LOCK_MODES.HARD;

/** Угроза городу «скоро» и выше: уровень NEXT_ROUND/THIS_ROUND или враг в двух ходах */
export const isCityThreatAtLeastSoon = (cityThreat) => Boolean(cityThreat)
  && (cityThreat.level > CITY_THREAT_LEVELS.NONE || cityThreat.isSoon === true);

// Порядок срочности: этот раунд > следующий > «скоро» > нет угрозы
const calcThreatRank = (cityThreat) => {
  if (cityThreat.level === CITY_THREAT_LEVELS.THIS_ROUND) {
    return 3;
  }
  if (cityThreat.level === CITY_THREAT_LEVELS.NEXT_ROUND) {
    return 2;
  }
  return cityThreat.isSoon ? 1 : 0;
};

/**
 * Доктрина обороны бота с поправкой на бонус: боты с бонусом «большой» (явное
 * золото) и выше — на ступень строже (см. configs/botGarrison)
 */
export const getGarrisonDoctrine = (player, gameType) => {
  const baseDoctrine = player
    ? getFactionGarrisonDoctrine(player.factionId, checkIfW42CelectialsStormPlayer(player, gameType))
    : DEFAULT_GARRISON_DOCTRINE;
  const bonusProfile = getBotBonusProfile(player, gameType);
  // Сравнение с допуском: порог — та же интенсивность «большого» бонуса
  return bonusProfile && bonusProfile.intensity >= W35_GARRISON_STRICT_MIN_INTENSITY - 1e-9
    ? getStricterGarrisonDoctrine(baseDoctrine)
    : baseDoctrine;
};

/**
 * Действуют ли у бота гарнизоны G (назначение в перемещении, маневре и кампаниях
 * и G-найм): только у ботов с бонусом «большой» (явное золото) и выше — по ЧИСЛАМ
 * бонуса (getBotBonusProfile: бонус действует, интенсивность не ниже
 * W35_GARRISON_MIN_INTENSITY) — и не в legacy-группе A/B (isPreW35GarrisonPlayer).
 * Остальные боты (без бонуса, с бонусом ниже «большого», неявные боты рейтинга)
 * играют в этих местах прежним кодом дословно. «Прикрытый тыл» H считает
 * гарнизоны сам (selectGarrisonAssignments) и от этого не зависит
 */
export const checkIfW35GarrisonPlayer = (player, gameType) => {
  if (!player || isPreW35GarrisonPlayer(player.id)) {
    return false;
  }
  const bonusProfile = getBotBonusProfile(player, gameType);
  // Сравнение с допуском: порог — интенсивность «большого» бонуса
  return Boolean(bonusProfile) && bonusProfile.intensity >= W35_GARRISON_MIN_INTENSITY - 1e-9;
};

/**
 * Ближайший агрессивный сосед (волна 35, K): соперник (не нейтрал) с наибольшим
 * суммарным размером отрядов в maxDistance от кварталов своих городов; при
 * равенстве — ближний. null — у своих городов чужих отрядов нет.
 * Возвращает {playerId, color, strength, distance, unitHexKeys}.
 */
export const findNearestAggressiveNeighbor = ({ownQuarterKeys, units, players, currentPlayerId, maxDistance}) => {
  if (!ownQuarterKeys || ownQuarterKeys.length === 0) {
    return null;
  }
  const byOwner = new Map();
  (units || []).forEach((unit) => {
    if (unit.ownerId === currentPlayerId || unit.ownerId === NEUTRAL_PLAYER_ID) {
      return;
    }
    let distance = Infinity;
    for (const quarterKey of ownQuarterKeys) {
      const quarterDistance = getHexDistance(unit.hexKey, quarterKey);
      if (quarterDistance < distance) {
        distance = quarterDistance;
      }
    }
    if (distance > maxDistance) {
      return;
    }
    const entry = byOwner.get(unit.ownerId) || {strength: 0, distance: Infinity, unitHexKeys: []};
    entry.strength += unit.size;
    entry.distance = Math.min(entry.distance, distance);
    entry.unitHexKeys.push(unit.hexKey);
    byOwner.set(unit.ownerId, entry);
  });
  let best = null;
  byOwner.forEach((entry, ownerId) => {
    const owner = (players || []).find((player) => player.id === ownerId);
    if (!owner || owner.color === NEUTRAL_PLAYER_COLOR) {
      return;
    }
    if (!best || entry.strength > best.strength
      || (entry.strength === best.strength && entry.distance < best.distance)) {
      best = {playerId: ownerId, color: owner.color, ...entry};
    }
  });
  return best;
};

const minDistanceToKeys = (hexKey, keys) => {
  let best = Infinity;
  for (const key of keys) {
    const distance = getHexDistance(hexKey, key);
    if (distance < best) {
      best = distance;
    }
  }
  return best;
};

// Распределение суммы двух честных костей урона: [[сумма, вероятность], ...]
let twoDamageDiceSums = null;
const getTwoDamageDiceSums = () => {
  if (!twoDamageDiceSums) {
    const faces = getDamageDiceFaceOutcomes();
    const counts = new Map();
    faces.forEach((faceA) => faces.forEach((faceB) => {
      counts.set(faceA + faceB, (counts.get(faceA + faceB) || 0) + 1);
    }));
    twoDamageDiceSums = [...counts.entries()].map(([sum, count]) => [sum, count / (faces.length * faces.length)]);
  }
  return twoDamageDiceSums;
};

/**
 * Вероятность гибели отряда размера unitSize в ближнем бою, где по нему бьют
 * отряды размеров enemySizes: урон отряда — размер плюс две честные кости урона
 * (не ниже 0), весь урон — по этому отряду (как ожидаемый урон в перемещении)
 */
export const calcMeleeDeathChance = (unitSize, enemySizes) => {
  if (!enemySizes || enemySizes.length === 0) {
    return 0;
  }
  let damageChances = new Map([[0, 1]]);
  enemySizes.forEach((enemySize) => {
    const nextChances = new Map();
    damageChances.forEach((chance, damage) => {
      getTwoDamageDiceSums().forEach(([diceSum, diceChance]) => {
        const nextDamage = damage + Math.max(0, enemySize + diceSum);
        nextChances.set(nextDamage, (nextChances.get(nextDamage) || 0) + chance * diceChance);
      });
    });
    damageChances = nextChances;
  });
  let deathChance = 0;
  damageChances.forEach((chance, damage) => {
    if (damage >= unitSize) {
      deathChance += chance;
    }
  });
  return deathChance;
};

/**
 * Волна 35 (G): почти гарантированная гибель в собственном ближнем бою
 * (W35_GARRISON_DOOMED_DEATH_CHANCE) — держатель с таким боем город не удержит
 */
export const isMeleeDoomed = (unitSize, enemySizes) => (
  calcMeleeDeathChance(unitSize, enemySizes) >= W35_GARRISON_DOOMED_DEATH_CHANCE - 1e-9
);

// Держатель: сначала переживающие ожидаемый урон ближайшего врага; среди них —
// меньший (крупные остаются ударными), без выживших — крупнейший
const pickHolder = (candidates, expectedDamage, tieBreak) => {
  const survivors = candidates.filter((unit) => unit.size > expectedDamage);
  const pool = survivors.length > 0 ? survivors : candidates;
  return [...pool].sort((a, b) => {
    const tie = tieBreak ? tieBreak(a, b) : 0;
    if (survivors.length > 0) {
      return tie || a.size - b.size || String(a.id).localeCompare(String(b.id));
    }
    return tie || b.size - a.size || String(a.id).localeCompare(String(b.id));
  })[0];
};

/**
 * Чистое ядро назначения гарнизонов (без стора, для тестов).
 *  ownCities — свои города; cityThreats — оценки getCityCaptureThreats({withSoon}) в том же порядке;
 *  units — все отряды; currentPlayerId; doctrine — с поправкой на бонус; baseDoctrine — доктрина фракции (кап);
 *  enemyQuarterKeys — кварталы городов живых противников;
 *  ownCityCellsByCityKey — Map центральный квартал → Set клеток города (кварталы и строения);
 *  isPassable(hexKey) — клетка на поле и не гора/море;
 *  getNeighbors(hexKey); getMoveCells(unit) → Set клеток, куда отряд встанет этим ходом;
 *  movedUnitIds — отряды, уже сходившие на этом этапе;
 *  isFinalRound — последний раунд партии: найма и следующего раунда нет, город
 *    открыт только угрозой этого раунда (открытость по дистанции не считается);
 *  supplyReachCells — Set клеток в досягаемости своих ферм; null — голода нет
 *    (нет ферм, ЭКОАНАРХИЗМ, последний раунд);
 *  exposureMode — W35_GARRISON_EXPOSURE_MODES: 'distance' (открыт и при противнике
 *    рядом) или 'threat' (открыт только при угрозе «скоро» и выше).
 * Держателем не назначается отряд, который город не удержит: почти наверняка
 * погибнет в собственном ближнем бою при враге на кварталах или рядом с ними
 * (isMeleeDoomed) или не переживет фазу снабжения в городе, ни один квартал
 * которого не снабжается (W35_GARRISON_STARVATION_LOSS)
 */
export const assignGarrisons = ({
  ownCities,
  cityThreats,
  units,
  currentPlayerId,
  doctrine,
  baseDoctrine,
  enemyQuarterKeys,
  ownCityCellsByCityKey,
  isPassable,
  getNeighbors,
  getMoveCells,
  movedUnitIds = new Set(),
  isFinalRound = false,
  supplyReachCells = null,
  exposureMode = W35_GARRISON_EXPOSURE_MODE,
}) => {
  const isThreatMode = exposureMode === W35_GARRISON_EXPOSURE_MODES.THREAT;
  const ownUnits = units.filter((unit) => unit.ownerId === currentPlayerId);
  const enemyUnits = units.filter((unit) => unit.ownerId !== currentPlayerId && unit.ownerId !== NEUTRAL_PLAYER_ID);
  const occupiedCells = new Set(units.map((unit) => unit.hexKey));

  const entries = ownCities.map((city, index) => {
    const cityThreat = cityThreats[index];
    const quarters = cityThreat ? cityThreat.quarters : getAllQuartersOfCity(city);
    const cityKey = city.centralQuarter;

    // Ближайший живой противник: отряд или квартал его города
    let enemyDistance = enemyQuarterKeys.length > 0
      ? Math.min(...quarters.map((quarterKey) => minDistanceToKeys(quarterKey, enemyQuarterKeys)))
      : Infinity;
    let nearestEnemyUnit = null;
    let nearestEnemyUnitDistance = Infinity;
    enemyUnits.forEach((enemy) => {
      const distance = minDistanceToKeys(enemy.hexKey, quarters);
      if (distance < nearestEnemyUnitDistance
        || (distance === nearestEnemyUnitDistance && nearestEnemyUnit && enemy.size > nearestEnemyUnit.size)) {
        nearestEnemyUnit = enemy;
        nearestEnemyUnitDistance = distance;
      }
    });
    enemyDistance = Math.min(enemyDistance, nearestEnemyUnitDistance);
    // Урон, который должен пережить держатель: ближайший живой отряд в зоне экспозиции
    const expectedDamage = nearestEnemyUnit && nearestEnemyUnitDistance <= W35_GARRISON_EXPOSURE_DISTANCE
      ? Math.max(0, nearestEnemyUnit.size - BOT_EXPECTED_DICE_PENALTY)
      : 0;

    const threatLevel = cityThreat ? cityThreat.level : CITY_THREAT_LEVELS.NONE;
    const isSoon = Boolean(cityThreat && cityThreat.isSoon);
    const isThreatened = cityThreat ? isCityThreatAtLeastSoon(cityThreat) : false;
    let exposure = GARRISON_EXPOSURE.SAFE;
    // В последнем раунде город открыт только угрозой этого раунда (NEXT_ROUND и
    // «скоро» там не сбудутся, найма нет), а враг рядом без нее город уже не
    // возьмет: открытость по дистанции заперла бы армию гарнизоном вместо
    // последних захватов
    if (isFinalRound ? threatLevel === CITY_THREAT_LEVELS.THIS_ROUND : isThreatened) {
      exposure = GARRISON_EXPOSURE.OPEN;
    } else if (!isFinalRound && enemyDistance <= W35_GARRISON_EXPOSURE_DISTANCE) {
      const isInterior = isInteriorCity({
        cityKey, quarters, ownCities, ownCityCellsByCityKey, enemyQuarterKeys, enemyUnits, isPassable, getNeighbors,
      });
      // Режим 'threat': противник рядом без угрозы «скоро» и выше город не открывает
      if (isInterior) {
        exposure = GARRISON_EXPOSURE.INTERIOR;
      } else if (!isThreatMode) {
        exposure = GARRISON_EXPOSURE.OPEN;
      }
    }
    return {
      cityKey,
      unitId: null,
      exposure,
      threatLevel,
      required: false,
      // Дополнительно для найма и отладки
      isSoon,
      quarters,
      cityWinPoints: cityThreat ? cityThreat.cityWinPoints : 0,
      enemyDistance,
      expectedDamage,
      holderIds: cityThreat ? cityThreat.holders.map((holder) => holder.id) : [],
      overCap: false,
      // Обязательный город: держатель есть или свой отряд встанет на свободный
      // квартал этим ходом (у overCap — если бы не кап); null — город не обязателен
      reachable: null,
      threatRank: cityThreat ? calcThreatRank(cityThreat) : 0,
      // Служебные (в ответ не попадают): враг на кварталах или смежен им — бой
      // держателя будет в фазе бота; снабжается ли хоть один квартал
      doomEnemySize: nearestEnemyUnit && nearestEnemyUnitDistance <= 1 ? nearestEnemyUnit.size : null,
      isSupplied: !supplyReachCells || quarters.some((quarterKey) => supplyReachCells.has(quarterKey)),
    };
  });

  const openEntries = entries.filter((entry) => entry.exposure === GARRISON_EXPOSURE.OPEN);
  const pickMostValuable = (candidates) => candidates.reduce((best, entry) => (
    !best || entry.cityWinPoints > best.cityWinPoints
    || (entry.cityWinPoints === best.cityWinPoints && entry.enemyDistance < best.enemyDistance)
      ? entry
      : best
  ), null);
  if (doctrine === GARRISON_DOCTRINES.ECONOMIC) {
    openEntries.forEach((entry) => {
      entry.required = true;
    });
  } else if (doctrine === GARRISON_DOCTRINES.MIXED) {
    openEntries.forEach((entry) => {
      entry.required = entry.cityWinPoints >= W35_GARRISON_MIXED_MIN_CITY_WIN_POINTS
        || entry.threatLevel >= CITY_THREAT_LEVELS.NEXT_ROUND;
    });
    const mostValuableOpen = pickMostValuable(openEntries);
    if (mostValuableOpen) {
      mostValuableOpen.required = true;
    }
  } else {
    openEntries.forEach((entry) => {
      entry.required = entry.threatLevel >= CITY_THREAT_LEVELS.NEXT_ROUND;
    });
    const mostValuableThreatened = pickMostValuable(openEntries.filter((entry) => entry.threatRank > 0));
    if (mostValuableThreatened) {
      mostValuableThreatened.required = true;
    }
  }

  // Кап: агрессивная фракция — не больше доли отрядов; у всех хотя бы один ударный
  // отряд, если отрядов двое и больше
  const unitCount = ownUnits.length;
  let cap = baseDoctrine === GARRISON_DOCTRINES.AGGRESSIVE
    ? Math.floor(unitCount * W35_GARRISON_AGGRESSIVE_MAX_UNIT_SHARE)
    : unitCount;
  if (unitCount >= 2) {
    cap = Math.min(cap, unitCount - 1);
  }

  const required = entries.filter((entry) => entry.required).sort((a, b) => (
    b.threatRank - a.threatRank
    || b.cityWinPoints - a.cityWinPoints
    || a.enemyDistance - b.enemyDistance
    || String(a.cityKey).localeCompare(String(b.cityKey))
  ));
  // Отряды на кварталах обязательных городов — держатели своего города, в чужой не уходят
  const reservedUnitIds = new Set(required.flatMap((entry) => entry.holderIds));
  const assignedUnitIds = new Set();
  let assignedCount = 0;
  // Держатель, который город не удержит: обреченный в своем бою (враг на
  // кварталах или смежен им) или голодающий (город вне снабжения, отряд не
  // переживет фазу снабжения). Без других кандидатов город остается без
  // держателя — его закрывает G-найм
  const canHoldCity = (entry, unit) => !(entry.doomEnemySize !== null && isMeleeDoomed(unit.size, [entry.doomEnemySize]))
    && !(!entry.isSupplied && unit.size <= W35_GARRISON_STARVATION_LOSS && !isUnitInsectoid(unit));
  // Свободные свои отряды, которые этим ходом встанут на свободный квартал города
  const findReachers = (entry, freeQuarters) => (freeQuarters.length === 0 ? [] : ownUnits.filter((unit) => {
    if (assignedUnitIds.has(unit.id) || reservedUnitIds.has(unit.id) || movedUnitIds.has(unit.id)
      || !canHoldCity(entry, unit)
    ) {
      return false;
    }
    const speed = (getUnitTypeConfig(unit.type)?.speed || 2) + (unit.additionalSupply === true ? 1 : 0);
    if (minDistanceToKeys(unit.hexKey, freeQuarters) > speed) {
      return false;
    }
    const moveCells = getMoveCells(unit);
    return freeQuarters.some((quarterKey) => moveCells.has(quarterKey));
  }));
  required.forEach((entry) => {
    const freeQuarters = entry.quarters.filter((quarterKey) => !occupiedCells.has(quarterKey));
    if (assignedCount >= cap) {
      entry.overCap = true;
      // Найм держателя (G-найм): дотянется ли до города хоть кто-то, если бы не кап
      entry.reachable = findReachers(entry, freeQuarters).length > 0;
      return;
    }
    const onQuarter = ownUnits.filter((unit) => entry.holderIds.includes(unit.id) && !assignedUnitIds.has(unit.id)
      && canHoldCity(entry, unit));
    let holder = null;
    if (onQuarter.length > 0) {
      holder = pickHolder(onQuarter, entry.expectedDamage);
    } else {
      const reachers = findReachers(entry, freeQuarters);
      if (reachers.length > 0) {
        const distanceOf = (unit) => minDistanceToKeys(unit.hexKey, freeQuarters);
        holder = pickHolder(reachers, entry.expectedDamage, (a, b) => distanceOf(a) - distanceOf(b));
      }
    }
    entry.reachable = Boolean(holder);
    if (holder) {
      entry.unitId = holder.id;
      assignedUnitIds.add(holder.id);
      assignedCount += 1;
    }
  });

  return entries.map(({threatRank, doomEnemySize, isSupplied, ...entry}) => entry);
};

/**
 * Внутренний город: все подходы прикрыты своими. Либо кольцо клеток вокруг
 * кварталов целиком из клеток ДРУГИХ своих городов (непроходимые — горы, море,
 * край поля — не в счет), либо от каждого живого противника в зоне экспозиции
 * (отряда или квартала) другой свой город ближе, чем этот. Врага в двух ходах у
 * такого города нет — это проверено раньше (угроза «скоро» делает город открытым).
 * Экспорт — для фронтира волны 37 (checkIfW37FrontierCity в evaluation/w37Threat)
 */
export const isInteriorCity = ({
  cityKey, quarters, ownCities, ownCityCellsByCityKey, enemyQuarterKeys, enemyUnits, isPassable, getNeighbors,
}) => {
  const quarterSet = new Set(quarters);
  const otherOwnCells = new Set();
  ownCities.forEach((otherCity) => {
    if (otherCity.centralQuarter === cityKey) {
      return;
    }
    (ownCityCellsByCityKey.get(otherCity.centralQuarter) || []).forEach((cellKey) => otherOwnCells.add(cellKey));
  });
  if (otherOwnCells.size === 0) {
    return false;
  }
  let isRingClosed = true;
  for (const quarterKey of quarters) {
    for (const adjKey of getNeighbors(quarterKey)) {
      if (!quarterSet.has(adjKey) && isPassable(adjKey) && !otherOwnCells.has(adjKey)) {
        isRingClosed = false;
        break;
      }
    }
    if (!isRingClosed) {
      break;
    }
  }
  if (isRingClosed) {
    return true;
  }
  const otherQuarters = ownCities
    .filter((otherCity) => otherCity.centralQuarter !== cityKey)
    .flatMap((otherCity) => getAllQuartersOfCity(otherCity));
  const sources = [...enemyQuarterKeys, ...enemyUnits.map((unit) => unit.hexKey)];
  // Квартал другого своего города прикрывает от источника, если лежит на кратчайшем
  // пути от него к городу (с запасом в клетку): ближе к источнику, и обход через
  // него не длиннее прямого пути. Просто «другой город ближе к врагу» не годится:
  // два города по разные стороны от соседа прикрывали бы друг друга
  return sources.every((sourceKey) => {
    const ownDistance = minDistanceToKeys(sourceKey, quarters);
    if (ownDistance > W35_GARRISON_EXPOSURE_DISTANCE) {
      return true;
    }
    return otherQuarters.some((otherQuarterKey) => {
      const sourceToOther = getHexDistance(sourceKey, otherQuarterKey);
      return sourceToOther < ownDistance
        && sourceToOther + minDistanceToKeys(otherQuarterKey, quarters) <= ownDistance + 1;
    });
  });
};

// Кэш на одну позицию: найм вызывает назначение на каждом тике этапа, а
// перемещение — один раз за ход (strategy.w35Garrisons). Ключ — все, от чего
// зависит результат: игрок, фаза/этап/раунд, порядок хода, сходившие отряды и
// отряды поля; плюс ссылки на клетки поля и кэш городов
let assignmentsMemo = null;

/**
 * Назначение гарнизонов по доктрине фракции (волна 35, G) — API для найма и
 * перемещения. Чистая функция состояния (кэш на позицию не влияет на результат).
 *
 * selectGarrisonAssignments(state, playerId) → Array (по своим городам, в порядке кэша городов) из
 *   {cityKey: string — центральный квартал своего города,
 *    unitId: string|null — держатель: свой отряд на квартале города или ближайший,
 *      кто встанет на свободный квартал этим ходом (предпочтительно переживающий
 *      ожидаемый урон ближайшего врага); null — держателя нет (в том числе
 *      все кандидаты обречены в своем бою или голодают — см. assignGarrisons),
 *    exposure: 'open'|'interior'|'safe' (GARRISON_EXPOSURE),
 *    threatLevel: CITY_THREAT_LEVELS.* — уровень угрозы волны 32,
 *    required: boolean — доктрина требует гарнизон в этом городе;
 *    дополнительно: isSoon — враг встает на квартал за два хода, quarters,
 *      cityWinPoints, enemyDistance — до ближайшего живого противника,
 *      expectedDamage — ожидаемый урон ближайшего врага, holderIds — свои отряды
 *      на кварталах, overCap — гарнизон не назначен из-за капа числа гарнизонов,
 *      reachable — у обязательного города держатель есть или свой отряд встанет на
 *      свободный квартал этим ходом (у overCap — если бы не кап); null у прочих}.
 * required && unitId === null — городу нужен держатель, которого нет: кандидат
 * на найм на казарме этого города (при overCap — отрядов не хватает на все
 * обязательные города); при reachable === false до города не дойдет никто —
 * его закрывает найм держателя (decideRecruitmentStep).
 *
 * Доктрины: экономическая — все открытые города; смешанная — открытые от
 * W35_GARRISON_MIXED_MIN_CITY_WIN_POINTS очков или с угрозой NEXT_ROUND и выше,
 * плюс самый ценный открытый; агрессивная — угроза NEXT_ROUND и выше и самый
 * ценный город под угрозой «скоро» и выше. Боты с бонусом «большой» и выше — на
 * ступень строже. Внутренний город гарнизона не требует. В последнем раунде
 * партии открыт и требует гарнизона только город под угрозой этого раунда.
 * Открытость — по режиму W35_GARRISON_EXPOSURE_MODE (калибровка:
 * globalThis.__w35GarrisonExposureMode); options.exposureMode — явный режим
 * вызывающего кода (H оценивает тыл по своему режиму, см. configs/botGarrison).
 */
export const selectGarrisonAssignments = (state, playerId, {exposureMode: explicitExposureMode = null} = {}) => {
  const players = selectPlayers(state) || [];
  const player = players.find((candidate) => candidate.id === playerId);
  const citiesCache = selectCitiesCache(state);
  const ownCities = player ? (citiesCache?.[player.color] || []) : [];
  if (!player || ownCities.length === 0) {
    return [];
  }
  const units = selectUnits(state) || [];
  const hexes = selectHexes(state);
  const pendingMovementActions = selectPendingMovementActions(state) || [];
  const gameType = selectGameType(state);
  const exposureMode = explicitExposureMode || getGarrisonExposureMode();
  const key = [
    exposureMode,
    playerId, selectRound(state), selectPhase(state), selectWarPhaseStage(state), gameType, selectFinalRound(state),
    (selectTurnOrder(state) || []).join(','), (selectPhaseCompletedPlayers(state) || []).join(','),
    selectNextInitiativePlayerId(state), player.policy?.id || '',
    pendingMovementActions.map((action) => action.unitId).join(','),
    units.map((unit) => `${unit.id}@${unit.hexKey}:${unit.type}:${unit.size}:${unit.additionalSupply ? 1 : 0}`).join('|'),
  ].join('#');
  const gridRenderer = getBotGridRenderer(state);
  if (assignmentsMemo && assignmentsMemo.key === key && assignmentsMemo.hexes === hexes
    && assignmentsMemo.citiesCache === citiesCache && assignmentsMemo.gridRenderer === gridRenderer) {
    return assignmentsMemo.result.map((entry) => ({...entry}));
  }

  const {cityThreats} = getCityCaptureThreats(state, player, {withSoon: true});
  const enemyQuarterKeys = Object.entries(citiesCache || {})
    .filter(([color]) => color !== player.color && color !== NEUTRAL_PLAYER_COLOR)
    .flatMap(([, cities]) => (cities || []).flatMap((city) => getAllQuartersOfCity(city)));
  const ownCityCellsByCityKey = new Map(ownCities.map((city) => [city.centralQuarter, getCityHexKeys(city)]));
  const trapDeathHexKeys = selectGoblinsTrapDeathHexKeys(state);
  const moveCellsByUnitId = new Map();
  // Голод держателя — по досягаемости своих ферм, как штраф снабжения в
  // перемещении; после последнего раунда фазы снабжения нет
  const isFinalRound = checkIfFinalRound(state);
  const farmHexKeys = getFarmsOfPlayerCities(citiesCache, player.color);
  const supplyReachCells = !isFinalRound && farmHexKeys.length > 0 && !isPlayerPolicy(player, POLICIES.ECOANARCHISM)
    ? calcSupplyReachCells(farmHexKeys, hexes, gridRenderer)
    : null;
  const result = assignGarrisons({
    ownCities,
    cityThreats,
    units,
    currentPlayerId: playerId,
    doctrine: getGarrisonDoctrine(player, gameType),
    baseDoctrine: getFactionGarrisonDoctrine(player.factionId, checkIfW42CelectialsStormPlayer(player, gameType)),
    enemyQuarterKeys,
    ownCityCellsByCityKey,
    isPassable: (hexKey) => Boolean(gridRenderer.hexes[hexKey]) && !isHexTokenIsBlockerForMovement(hexes[hexKey]),
    getNeighbors: (hexKey) => getNeighborHexKeys(hexKey, gridRenderer),
    getMoveCells: (unit) => {
      if (!moveCellsByUnitId.has(unit.id)) {
        const {cells} = calculateAvailableMovementCells(gridRenderer, hexes, units, unit, players, trapDeathHexKeys);
        moveCellsByUnitId.set(unit.id, new Set(cells));
      }
      return moveCellsByUnitId.get(unit.id);
    },
    movedUnitIds: new Set(pendingMovementActions.map((action) => action.unitId)),
    isFinalRound,
    supplyReachCells,
    exposureMode,
  });
  assignmentsMemo = {key, hexes, citiesCache, gridRenderer, result};
  return result.map((entry) => ({...entry}));
};

/**
 * Назначение на ход войны бота: считается на первом тике перемещения раунда и
 * хранится в strategy.w35Garrisons ({round, assignments}); дальше в этом раунде
 * не пересчитывается — гарнизон не «прыгает» между городами посреди хода.
 * Снимается гарнизон пересчетом в следующем ходу, когда город перестал быть открытым
 */
export const ensureGarrisonAssignments = (state, player) => {
  const strategy = getBotStrategy(player.id);
  const round = selectRound(state);
  if (!strategy.w35Garrisons || strategy.w35Garrisons.round !== round) {
    strategy.w35Garrisons = {round, assignments: selectGarrisonAssignments(state, player.id)};
  }
  return strategy.w35Garrisons.assignments;
};

/** Назначение этого раунда из стратегии (маневр читает то, что посчитало перемещение) */
export const getStoredGarrisonAssignments = (strategy, round) => (
  strategy && strategy.w35Garrisons && strategy.w35Garrisons.round === round
    ? strategy.w35Garrisons.assignments
    : null
);

/**
 * Отряд-гарнизон → {cityKey, quarters: Set, holdValue}; null — гарнизонов нет.
 * holdValue — ценность держателя города (calcCityHolderValue по очкам города):
 * удержание квартала гарнизоном в режиме 'soft'
 */
export const buildGarrisonUnitIndex = (assignments) => {
  if (!assignments) {
    return null;
  }
  const index = new Map();
  assignments.forEach((entry) => {
    if (entry.required && entry.unitId) {
      index.set(entry.unitId, {
        cityKey: entry.cityKey,
        quarters: new Set(entry.quarters),
        holdValue: calcCityHolderValue(entry.cityWinPoints || 0),
      });
    }
  });
  return index.size > 0 ? index : null;
};
